/**
 *
 * Appointment
 *
 */

import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { Component, Fragment, Suspense } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { Route /* , withRouter, Redirect */, Switch } from 'react-router-dom';
import { withLastLocation } from 'react-router-last-location';
import { Elements, StripeProvider } from 'react-stripe-elements';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import Config from 'app.config';
import injectSaga from 'utils/injectSaga';
import '../../css/appointment.css';
import PrivateRoute from '../App/PrivateRoute';
import makeSelectMembership from '../Membership/selectors';
import { makeSelectUser } from '../User/selectors';
import { fetchAvailableTherapists } from './actions';

import LoadingScreen from '../../components/LoadingIndicator';
import GrinInit from '../../utils/third-party-plugins/grin-init';
import saga from './saga';
import makeSelectAppointment from './selectors';
import lazyLoad from '../../utils/react-lazy-load';
import { LOCATION_NAME_URL_MAPPER } from '../../utils/common';
import EligibleRoute from '../App/EligibleRoute';
import ImpersonateUser from '../../components/impersonateUser';
import RequiredGovtInfo from './required_govt_info';

const ConfirmDetails = lazyLoad(() => import('./confirm_details'));
const Signup = lazyLoad(() => import('../Signup'));
const Login = lazyLoad(() => import('../Login'));
const BookingConfirmation = lazyLoad(() => import('./booked'));
const Abandonmentpage = lazyLoad(() => import('../Abandonmentpage'));
const FirstTrimester = lazyLoad(() => import('./first_trimester'));
const CancelAppointment = lazyLoad(() => import('./cancel_appointment'));
const FailedPayment = lazyLoad(() => import('./failed_payment'));
const AppointmentLocations = lazyLoad(() => import('./locations'));
const Services = lazyLoad(() => import('./services'));
const MassageTypes = lazyLoad(() => import('./massage_types'));
const LocationInfo = lazyLoad(() => import('./location_info'));
const PickSqueezer = lazyLoad(() => import('./therapist_picker'));
const ErrorContent = lazyLoad(() => import('../../components/ErrorContent'));

/* eslint-disable react/prefer-stateless-function */
export class Appointment extends Component {
  render() {
    const isMember =
      get(queryString.parse(this.props.location.search), 'is_membership') ===
      'true';

    const content = (
      <Suspense fallback={<LoadingScreen hangTight />}>
        <Switch>
          <PrivateRoute
            exact
            path="/appointment/confirm"
            toPath="/appointment/register"
            injectStripe
            {...this.props}
            component={ConfirmDetails}
          />
          <Route
            exact
            path="/appointment/register"
            render={() => (
              <Signup
                headerProps={{ showBackButton: true, showCloseButton: true }}
                footerProps={{ showItems: false }}
                headerText="CREATE ACCOUNT"
                submitBtnText="Continue"
                mobileHeaderText="WHO'S THIS FOR?"
                subHeaderText={
                  isMember
                    ? 'Who is this membership for'
                    : 'Who is this massage for?'
                }
                showLoginLink
                loginPath="/appointment/login"
                {...this.props}
              />
            )}
          />
          <Route
            exact
            path="/appointment/login"
            render={() => (
              <Login
                headerProps={{ showBackButton: true, showCloseButton: true }}
                footerProps={{ showItems: false }}
                {...this.props}
              />
            )}
          />
          <Route
            exact
            path="/appointment/booked"
            render={() => <BookingConfirmation {...this.props} />}
          />
          <Route
            exact
            path="/appointment/requiredGovtInfo"
            render={() => <RequiredGovtInfo {...this.props} />}
          />
          <Route
            exact
            path="/appointment/exit"
            render={() => <Abandonmentpage {...this.props} />}
          />

          <Route
            exact
            path="/appointment/pregnancy/first-trimester"
            render={() => <FirstTrimester {...this.props} />}
          />

          <Route
            exact
            path="/appointment/cancel/:spabooker_id"
            render={() => <CancelAppointment {...this.props} />}
          />
          <Route
            exact
            path="/appointment/locations/impersonateUser"
            render={() => <ImpersonateUser {...this.props} />}
          />
          <Route
            exact
            path="/appointment/payment-failed"
            render={() => (
              <StripeProvider apiKey={Config.stripeApiKey}>
                <Elements>
                  <FailedPayment {...this.props} />
                </Elements>
              </StripeProvider>
            )}
          />

          <EligibleRoute
            exact
            path="/appointment/locations"
            {...this.props}
            component={AppointmentLocations}
          />
          <EligibleRoute
            exact
            path="/appointment/location/:locationId/services/:locationName"
            {...this.props}
            component={Services}
          />
          <EligibleRoute
            exact
            path="/appointment/therapist/:therapistId/services"
            component={Services}
            {...this.props}
          />
          <EligibleRoute
            exact
            path="/appointment/therapist/:therapistId/massage-types"
            component={MassageTypes}
            {...this.props}
          />
          <EligibleRoute
            exact
            path="/appointment/location/:locationId/more-info/:locationName"
            component={LocationInfo}
            {...this.props}
          />
          <EligibleRoute
            exact
            path="/appointment/location/:locationId/massage-types/:locationName"
            component={MassageTypes}
            {...this.props}
          />
          <EligibleRoute
            exact
            path="/appointment/location/:locationId/pick-squeezer/:locationName"
            component={PickSqueezer}
            {...this.props}
          />
          <Route path="*" component={ErrorContent} />
        </Switch>
      </Suspense>
    );
    const { pathname } = this.props.location;
    const urlName = pathname.substring(pathname.lastIndexOf('/') + 1);
    const location =
      LOCATION_NAME_URL_MAPPER.find(loc => loc.url_name === urlName) || {};
    const title = `A Way Better Massage Experience | ${location.city ||
      'Studio City'}`;
    // eslint-disable-next-line no-console
    return (
      <Fragment>
        <Helmet
          script={GrinInit(
            localStorage.getItem(localStorage.getItem('integrity')),
          )}
        >
          <title>{title}</title>
          <meta
            name="description"
            content="Get a feel-good massage from Squeeze. Use our app to save your massage preferences to your profile so your therapist knows what you want, each time. Book today! | One, But More to Come!"
          />
        </Helmet>
        {content}
      </Fragment>
    );
  }
}

Appointment.propTypes = {
  location: PropTypes.object,
  appointmentData: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  appointmentData: makeSelectAppointment(),
  user: makeSelectUser(),
  membershipData: makeSelectMembership(),
});

const withSagaFetchAvailableTherapists = injectSaga({
  key: 'fetchAvailableTherapists',
  saga: saga.watchFetchAvailableTherapists,
});

const withConnect = connect(
  mapStateToProps,
  { fetchAvailableTherapists },
);

export default compose(
  withConnect,
  withSagaFetchAvailableTherapists,
  withLastLocation,
)(Appointment);
